const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

async function request(path, { method = "GET", body, headers = {} } = {}) {
    const opts = {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
    };
    if (body !== undefined) opts.body = JSON.stringify(body);
    const res = await fetch(`${API_BASE}${path}`, opts);
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = text; }
    if (!res.ok) {
        const detail = (data && data.detail) || res.statusText || "Request failed";
        const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return data;
}

const qs = (params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "") return;
        p.set(k, v);
    });
    const s = p.toString();
    return s ? `?${s}` : "";
};

export const api = {
    me: () => request("/auth/me"),
    register: (payload) => request("/auth/register", { method: "POST", body: payload }),
    verifyEmail: (token) => request("/auth/verify-email", { method: "POST", body: { token } }),
    login: (email, password) => request("/auth/login", { method: "POST", body: { email, password } }),
    logout: () => request("/auth/logout", { method: "POST" }),
    forgotPassword: (email) => request("/auth/forgot-password", { method: "POST", body: { email } }),
    resetPassword: (token, new_password) =>
        request("/auth/reset-password", { method: "POST", body: { token, new_password } }),
    googleSession: (session_id, role) =>
        request("/auth/google/session", { method: "POST", body: { session_id, role } }),
    devVerificationToken: (email) => request(`/dev/verification-token/${encodeURIComponent(email)}`),
    devGrantAccess: (email, plan = "monthly") =>
        request(`/dev/grant-access/${encodeURIComponent(email)}?plan=${plan}`, { method: "POST" }),
    devSimulateMonthlySubscription: (email) =>
        request(`/dev/simulate-monthly-subscription/${encodeURIComponent(email)}`, { method: "POST" }),
    billingCheckout: (plan, origin_url) =>
        request("/billing/checkout-session", { method: "POST", body: { plan, origin_url } }),
    billingPortal: () => request("/billing/portal", { method: "POST" }),
    billingStatus: () => request("/billing/status"),
    billingPublicConfig: () => request("/billing/public-config"),
    billingUpgradePreview: () => request("/billing/upgrade-preview"),
    billingUpgrade: () => request("/billing/upgrade", { method: "POST" }),
    // Curriculum
    getCurriculum: () => request("/curriculum"),
    getLesson: (id) => request(`/curriculum/lessons/${id}`),
    postLessonProgress: (id, watched_seconds, watched_percent) =>
        request(`/curriculum/lessons/${id}/progress`, { method: "POST", body: { watched_seconds, watched_percent } }),
    markLessonComplete: (id) => request(`/curriculum/lessons/${id}/complete`, { method: "POST" }),
    getQuiz: (id) => request(`/curriculum/quizzes/${id}`),
    attemptQuiz: (id, answers) => request(`/curriculum/quizzes/${id}/attempt`, { method: "POST", body: { answers } }),
    listQuizAttempts: (id) => request(`/curriculum/quizzes/${id}/attempts`),
    // Journal
    listJournalEntries: (params = {}) => request(`/journal/entries${qs(params)}`),
    getJournalEntry: (id) => request(`/journal/entries/${id}`),
    createJournalEntry: (payload) => request("/journal/entries", { method: "POST", body: payload }),
    updateJournalEntry: (id, patch) => request(`/journal/entries/${id}`, { method: "PATCH", body: patch }),
    deleteJournalEntry: (id) => request(`/journal/entries/${id}`, { method: "DELETE" }),
    journalStats: () => request("/journal/stats"),
    listJournalComments: (entryId) => request(`/journal/entries/${entryId}/comments`),
    addJournalComment: (entryId, body) => request(`/journal/entries/${entryId}/comments`, { method: "POST", body: { body } }),
    // Admin journal
    adminListJournalEntries: (params = {}) => request(`/admin/journal/entries${qs(params)}`),
    adminListJournalUsers: () => request("/admin/journal/users"),
    // Admin console (Phase 4)
    adminOverview: () => request("/admin/overview"),
    adminListStudents: (params = {}) => request(`/admin/students${qs(params)}`),
    adminStudentDetail: (userId) => request(`/admin/students/${userId}`),
    adminCancelSubscription: (userId, reason) =>
        request(`/admin/students/${userId}/cancel-subscription`, { method: "POST", body: { reason } }),
    adminForceRefund: (userId, confirm_word, reason) =>
        request(`/admin/students/${userId}/force-refund`, { method: "POST", body: { confirm_word, reason } }),
    adminUnlockPhase: (userId, phase_id, reason) =>
        request(`/admin/users/${userId}/phase-unlock`, { method: "POST", body: { phase_id, reason } }),
    // Affiliate (Phase 5)
    trackAffiliateClick: (ref_code, landing_path) =>
        request("/affiliate/track-click", { method: "POST", body: { ref_code, landing_path } }),
    verifyRefCode: (code) => request(`/affiliate/verify-ref/${encodeURIComponent(code)}`),
    affiliateMe: () => request("/affiliate/me"),
    affiliateUpdateMe: (patch) => request("/affiliate/me", { method: "PATCH", body: patch }),
    affiliateDashboard: () => request("/affiliate/dashboard"),
    affiliateCommissions: (params = {}) => request(`/affiliate/commissions${qs(params)}`),
    affiliatePayouts: () => request("/affiliate/payouts"),
    affiliateReferrals: () => request("/affiliate/referrals"),
    adminListAffiliates: (params = {}) => request(`/admin/affiliates${qs(params)}`),
    adminAffiliateDetail: (id) => request(`/admin/affiliates/${id}`),
    adminUpdateAffiliate: (id, patch) => request(`/admin/affiliates/${id}`, { method: "PATCH", body: patch }),
    adminCreateAffiliate: (payload) => request("/admin/affiliates", { method: "POST", body: payload }),
    adminDeactivateAffiliate: (id, confirm_word, reason) =>
        request(`/admin/affiliates/${id}/deactivate`, { method: "POST", body: { confirm_word, reason } }),
    adminReactivateAffiliate: (id, reason) =>
        request(`/admin/affiliates/${id}/reactivate`, { method: "POST", body: { reason } }),
    adminListCommissions: (params = {}) => request(`/admin/commissions${qs(params)}`),
    adminCreatePayout: (payload) => request("/admin/payouts", { method: "POST", body: payload }),
    adminListPayouts: (params = {}) => request(`/admin/payouts${qs(params)}`),
    adminLockPhase: (userId, phase_id, reason) =>
        request(`/admin/users/${userId}/phase-lock`, { method: "POST", body: { phase_id, reason } }),
    // Admin curriculum
    adminGetCurriculum: () => request("/admin/curriculum"),
    adminPatchPhase: (id, patch) => request(`/admin/phases/${id}`, { method: "PATCH", body: patch }),
    adminCreateModule: (payload) => request("/admin/modules", { method: "POST", body: payload }),
    adminPatchModule: (id, patch) => request(`/admin/modules/${id}`, { method: "PATCH", body: patch }),
    adminDeleteModule: (id) => request(`/admin/modules/${id}`, { method: "DELETE" }),
    adminCreateLesson: (payload) => request("/admin/lessons", { method: "POST", body: payload }),
    adminPatchLesson: (id, patch) => request(`/admin/lessons/${id}`, { method: "PATCH", body: patch }),
    adminDeleteLesson: (id) => request(`/admin/lessons/${id}`, { method: "DELETE" }),
    adminPatchQuiz: (id, patch) => request(`/admin/quizzes/${id}`, { method: "PATCH", body: patch }),
    // Lesson attachments (Fix F)
    adminUploadLessonAttachment: async (lessonId, file) => {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API_BASE}/admin/lessons/${lessonId}/attachments`, {
            method: "POST", credentials: "include", body: fd,
        });
        const text = await res.text();
        let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = text; }
        if (!res.ok) {
            const detail = (data && data.detail) || res.statusText;
            const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
            err.status = res.status; err.data = data; throw err;
        }
        return data;
    },
    adminDeleteLessonAttachment: (lessonId, attachmentId) =>
        request(`/admin/lessons/${lessonId}/attachments/${attachmentId}`, { method: "DELETE" }),
    lessonAttachmentDownloadUrl: (lessonId, attachmentId) =>
        `${API_BASE}/curriculum/lessons/${lessonId}/attachments/${attachmentId}`,
    adminUploadInstructorPortrait: async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API_BASE}/admin/site-settings/portrait`, {
            method: "POST", credentials: "include", body: fd,
        });
        const text = await res.text();
        let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = text; }
        if (!res.ok) {
            const detail = (data && data.detail) || res.statusText;
            const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
            err.status = res.status; err.data = data; throw err;
        }
        return data;
    },

    // Live sessions — admin
    adminListLiveSessions: () => request("/admin/live-sessions"),
    adminCreateLiveSession: (payload) => request("/admin/live-sessions", { method: "POST", body: payload }),
    adminPatchLiveSession: (id, patch) => request(`/admin/live-sessions/${id}`, { method: "PATCH", body: patch }),
    adminArchiveLiveSession: (id, archive_url) => request(`/admin/live-sessions/${id}/archive`, { method: "POST", body: { archive_url } }),
    adminDeleteLiveSession: (id) => request(`/admin/live-sessions/${id}`, { method: "DELETE" }),
    // Live sessions — member
    liveSessionsUpcoming: () => request("/live-sessions/upcoming"),
    liveSessionsArchive: () => request("/live-sessions"),
    liveSessionDetail: (id) => request(`/live-sessions/${id}`),
    // Admin email log
    adminEmailLog: (params = {}) => {
        const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
        return request(`/admin/email-log${qs ? `?${qs}` : ""}`);
    },
    // Admin pricing
    adminGetPricing: () => request("/admin/pricing"),
    adminUpdatePricing: (plan_key, patch) => request(`/admin/pricing/${plan_key}`, { method: "PUT", body: patch }),
    adminValidatePricing: (plan_key) => request(`/admin/pricing/${plan_key}/validate`, { method: "POST" }),
    // Admin blog
    adminListBlog: () => request("/admin/blog-posts"),
    adminGetBlog: (id) => request(`/admin/blog-posts/${id}`),
    adminCreateBlog: (payload) => request("/admin/blog-posts", { method: "POST", body: payload }),
    adminUpdateBlog: (id, patch) => request(`/admin/blog-posts/${id}`, { method: "PATCH", body: patch }),
    adminDeleteBlog: (id) => request(`/admin/blog-posts/${id}`, { method: "DELETE" }),
    // Admin settings
    adminGetSiteSettings: () => request("/admin/site-settings"),
    adminUpdateSiteSettings: (patch) => request("/admin/site-settings", { method: "PUT", body: patch }),
    // Admin audit log
    adminAuditLog: (params = {}) => request(`/admin/audit-log-full${qs(params)}`),
    // Public content
    publicPricing: () => request("/pricing"),
    publicSiteSettings: () => request("/site-settings"),
    publicBlogList: () => request("/blog"),
    publicBlogPost: (slug) => request(`/blog/${slug}`),
    // Testimonials (Phase B)
    publicTestimonials: () => request("/testimonials"),
    adminListTestimonials: () => request("/admin/testimonials"),
    adminCreateTestimonial: (payload) => request("/admin/testimonials", { method: "POST", body: payload }),
    adminUpdateTestimonial: (id, patch) => request(`/admin/testimonials/${id}`, { method: "PATCH", body: patch }),
    adminDeleteTestimonial: (id) => request(`/admin/testimonials/${id}`, { method: "DELETE" }),
    adminReorderTestimonials: (order) => request("/admin/testimonials/reorder", { method: "POST", body: { order } }),
};
